/**
 * CartItem → OrderItem 변환 유틸리티
 *
 * PaymentModal에서 주문 생성 시 장바구니 아이템을
 * BE CreateOrderRequest 형식으로 변환합니다.
 */
import type { CartItem } from '@/types';
import type { OrderItem, OrderItemOption } from '@/lib/api/types';

/**
 * CartItem.selectedOptions (Record<string, string[]>) →
 * OrderItemOption[] ({ option_id, choice_ids })
 */
function convertOptions(
  selectedOptions?: Record<string, string[]>
): OrderItemOption[] | undefined {
  if (!selectedOptions) return undefined;

  const options: OrderItemOption[] = [];
  for (const [groupId, choiceIds] of Object.entries(selectedOptions)) {
    const optionId = Number(groupId);
    const numericChoiceIds = choiceIds.map(Number).filter((n) => !isNaN(n));

    if (!isNaN(optionId) && optionId > 0 && numericChoiceIds.length > 0) {
      options.push({
        option_id: optionId,
        choice_ids: numericChoiceIds,
      });
    }
  }

  return options.length > 0 ? options : undefined;
}

/**
 * CartItem[] → OrderItem[] 변환
 *
 * menuItemId가 없는 아이템은 건너뜁니다 (Figma 목업 데이터 등).
 */
export function cartItemsToOrderItems(cartItems: CartItem[]): OrderItem[] {
  return cartItems
    .filter((item) => item.menuItemId)
    .map((item) => ({
      menu_id: Number(item.menuItemId),
      quantity: item.quantity,
      options: convertOptions(item.selectedOptions),
    }));
}

/**
 * CartItem[]에서 store_id 추출
 *
 * 장바구니의 첫 번째 아이템의 restaurantId를 사용합니다.
 * 여러 매장의 메뉴가 혼합된 경우 null을 반환합니다.
 */
export function extractStoreId(cartItems: CartItem[]): number | null {
  if (cartItems.length === 0) return null;

  const storeIds = new Set(
    cartItems.map((item) => item.restaurantId).filter(Boolean)
  );

  if (storeIds.size !== 1) return null;

  const storeId = Number(cartItems[0].restaurantId);
  return isNaN(storeId) ? null : storeId;
}
